import React from 'react';
import { useNavigate } from 'react-router-dom';
import { recipes } from '../data/recipes';
import { activities } from '../data/activities';
import { useTheme } from '../contexts/ThemeContext';

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  // Get recent posts combining both recipes and activities
  // Sort by date across both types
  const recentPosts = [
    ...recipes.map(recipe => ({
      type: 'Recipe',
      title: recipe.title,
      date: recipe.date,
      description: `A delicious ${recipe.cuisine} ${recipe.type}`,
      color: '#c86a3f',
      path: `/recipes/${recipe.id}`
    })),
    ...activities.map(activity => ({
      type: 'Activity',
      title: activity.title,
      date: activity.date,
      description: activity.description,
      color: '#6a9fc8',
      path: `/activities/${activity.id}`
    }))
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);

  const containerStyle: React.CSSProperties = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '2rem',
    paddingTop: '8rem',
    fontFamily: 'Georgia, serif',
    backgroundColor: isDarkMode ? '#000000' : '#fafaf9',
  };

  const heroStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '4rem',
  };

  const categoryGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    marginBottom: '4rem',
  };

  const categoryCardStyle = (gradientStart: string, gradientEnd: string): React.CSSProperties => ({
    padding: '2rem',
    borderRadius: '1rem',
    background: `linear-gradient(135deg, ${gradientStart}, ${gradientEnd})`,
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '1rem',
  });

  const recentPostsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '2rem',
  };

  const postCardStyle: React.CSSProperties = {
    backgroundColor: isDarkMode ? '#1a1a1a' : 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: isDarkMode ? '0 2px 4px rgba(255,255,255,0.05)' : '0 2px 4px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  };

  return (
    <div style={containerStyle}>
      {/* Hero Section */}
      <div style={heroStyle}>
        <h1 style={{ fontSize: '3.5rem', fontWeight: 'normal', marginBottom: '1rem', color: isDarkMode ? '#ffffff' : '#292524' }}>
          Blog
        </h1>
        <p style={{ fontSize: '1.25rem', color: isDarkMode ? '#a0a0a0' : '#78716c' }}>
          Exploring flavors and adventures, one story at a time
        </p>
      </div>

      {/* Category Cards */}
      <div style={categoryGridStyle}>
        {/* Recipes Card */}
        <div
          style={categoryCardStyle('#c86a3f', '#e8a87c')}
          onClick={() => {
            console.log('Navigating to recipes');
            navigate('/recipes');
          }}
          onMouseEnter={e => {
            const target = e.currentTarget;
            target.style.transform = 'translateY(-8px)';
            target.style.boxShadow = '0 12px 20px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={e => {
            const target = e.currentTarget;
            target.style.transform = 'translateY(0)';
            target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
          }}
        >
          <div style={{ fontSize: '3rem' }}></div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>Recipes</h2>
          <p>Explore my collection of favorite recipes and cooking experiments</p>
          <div style={{ marginTop: 'auto' }}>
            <span>{recipes.length} posts</span>
            <span style={{ marginLeft: '1rem' }}>→</span>
          </div>
        </div>

        {/* Activities Card */}
        <div
          style={categoryCardStyle('#6a9fc8', '#7cb8e8')}
          onClick={() => {
            console.log('Navigating to activities');
            navigate('/activities');
          }}
          onMouseEnter={e => {
            const target = e.currentTarget;
            target.style.transform = 'translateY(-8px)';
            target.style.boxShadow = '0 12px 20px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={e => {
            const target = e.currentTarget;
            target.style.transform = 'translateY(0)';
            target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
          }}
        >
          <div style={{ fontSize: '3rem' }}></div>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>Activities</h2>
          <p>Join me on exciting adventures and outdoor experiences</p>
          <div style={{ marginTop: 'auto' }}>
            <span>{activities.length} posts</span>
            <span style={{ marginLeft: '1rem' }}>→</span>
          </div>
        </div>
      </div>

      {/* Recent Posts Section */}
      <div style={{ marginBottom: '4rem' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 'normal', marginBottom: '2rem', color: isDarkMode ? '#ffffff' : '#292524' }}>
          Recent Posts
        </h2>
        <div style={recentPostsGridStyle}>
          {recentPosts.map((post, index) => (
            <div
              key={index}
              style={postCardStyle}
              onClick={() => {
                console.log('Navigating to post:', post.path);
                navigate(post.path);
              }}
              onMouseEnter={e => {
                const target = e.currentTarget;
                target.style.transform = 'translateY(-4px)';
                target.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={e => {
                const target = e.currentTarget;
                target.style.transform = 'translateY(0)';
                target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              }}
            >
              <div style={{ padding: '0.5rem 1rem', backgroundColor: post.color, color: 'white' }}>
                {post.type}
              </div>
              <div style={{ padding: '1.5rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'normal', marginBottom: '0.5rem', color: isDarkMode ? '#ffffff' : '#292524' }}>
                  {post.title}
                </h3>
                <p style={{ color: isDarkMode ? '#a0a0a0' : '#78716c', marginBottom: '1rem' }}>
                  {post.description}
                </p>
                <time style={{ color: isDarkMode ? '#808080' : '#78716c', fontSize: '0.875rem' }}>
                  {new Date(post.date).toLocaleDateString()}
                </time>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Blog;
